import { useState, useMemo } from 'react';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Gift,
  Instagram,
  Phone,
  Users,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useDebounce } from '@/hooks/useDebounce';
import { useLoyaltyParticipants } from './hooks';
import { LoyaltyCustomerHistoryDrawer } from './LoyaltyCustomerHistoryDrawer';

const PAGE_SIZE = 20;

const getInitials = (name: string | null): string => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

export const LoyaltyParticipantsTable = () => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [drawerCustomerId, setDrawerCustomerId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const debouncedSearch = useDebounce(search, 300);

  const effectivePage = useMemo(() => {
    // Reset to first page when search changes
    return page;
  }, [page]);

  const { data, isLoading, isFetching } = useLoyaltyParticipants(
    debouncedSearch,
    effectivePage,
    PAGE_SIZE,
  );

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const rows = data?.rows ?? [];

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const handleRowClick = (customerId: string) => {
    setDrawerCustomerId(customerId);
    setDrawerOpen(true);
  };

  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Buscar por nome, Instagram ou WhatsApp..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-9 h-9 text-sm"
            />
          </div>
          {data && (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {data.total.toLocaleString('pt-BR')} cliente{data.total !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-14" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center rounded-xl border border-dashed">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted mb-3">
              <Users className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">
              {debouncedSearch ? 'Nenhum cliente encontrado' : 'Nenhum cliente participando ainda'}
            </p>
            <p className="text-xs text-muted-foreground mt-1 max-w-xs">
              {debouncedSearch
                ? 'Tente outro termo de busca.'
                : 'Clientes aparecerão aqui quando começarem a acumular pontos em compras pagas.'}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border">
            <div className="divide-y">
              {rows.map((row) => (
                <button
                  key={row.customer_id}
                  type="button"
                  onClick={() => handleRowClick(row.customer_id)}
                  className="group flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-muted/40"
                >
                  <Avatar className="h-9 w-9 shrink-0">
                    <AvatarFallback className="text-xs font-semibold bg-[#EAEFFE] text-[#574BFB]">
                      {getInitials(row.name)}
                    </AvatarFallback>
                  </Avatar>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">
                        {row.name ?? 'Cliente sem nome'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {row.instagram && (
                        <span className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground">
                          <Instagram className="h-2.5 w-2.5" />
                          @{row.instagram.replace('@', '')}
                        </span>
                      )}
                      {row.whatsapp && (
                        <span className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground">
                          <Phone className="h-2.5 w-2.5" />
                          {row.whatsapp}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-right shrink-0">
                    <div className="inline-flex items-center gap-1 text-sm font-semibold text-[#574BFB]">
                      <Gift className="h-3.5 w-3.5" />
                      {row.balance.toLocaleString('pt-BR')}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Total: {row.lifetime_earned.toLocaleString('pt-BR')}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-1">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0 || isFetching}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              <ChevronLeft className="h-3.5 w-3.5 mr-1" />
              Anterior
            </Button>
            <span className="text-xs text-muted-foreground">
              Página {page + 1} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages - 1 || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Próxima
              <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        )}
      </div>

      <LoyaltyCustomerHistoryDrawer
        customerId={drawerCustomerId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </>
  );
};
